// Os controles de uma lista com filtros ligados ao `EstadoLista` (#93): o
// controle só mostra e alimenta o estado — quem guarda o valor, volta à
// página 1 e limpa é o estado. Assim "Limpar Filtros" vira `lista.limpar()`
// e cada controle se acerta sozinho no próximo render.

import { el } from "../ui.ts";
import { casaBusca } from "./_cadastro.ts";
import type { EstadoLista } from "./_estado-lista.ts";
import { multiSelect } from "./_multi-select.ts";

type Item = { id: string; nome: string };
/** Chaves de `F` cujo valor é uma seleção múltipla (`null` = todos). */
type ChaveSelecao<F> = { [K in keyof F]: F[K] extends string[] | null ? K : never }[keyof F];

export interface FiltroMultiplo {
  readonly element: HTMLElement;
  /** Troca os itens (a cada render) e mostra a seleção do estado. */
  atualizar(itens: readonly Item[]): void;
  /** Os ids que passam no filtro — todos, quando o estado diz `null`. */
  selecionados(): Set<string>;
}

/**
 * Filtro de seleção múltipla ligado ao estado: `null` no estado = todos
 * marcados (e marcar todos à mão volta a ser `null`, para não contar como
 * filtro ativo).
 */
export function filtroMultiplo<F extends Record<string, unknown>>(
  lista: EstadoLista<F>,
  chave: ChaveSelecao<F>,
  rotulo: string,
  aoMudar: () => void,
): FiltroMultiplo {
  let itens: readonly Item[] = [];
  const ms = multiSelect({
    rotulo,
    itens: [],
    aoMudar: () => {
      const sel = ms.selecionados();
      lista.mudarFiltro(chave, (sel.length === itens.length ? null : sel) as F[typeof chave]);
      aoMudar();
    },
  });
  return {
    element: ms.element,
    atualizar(novos) {
      itens = novos;
      ms.atualizarItens(novos);
      const v = lista.filtro(chave) as string[] | null;
      if (v == null) ms.marcarTodos();
      else ms.definirSelecionados(v);
    },
    selecionados() {
      const v = lista.filtro(chave) as string[] | null;
      return new Set(v ?? itens.map((i) => i.id));
    },
  };
}

/** Campo de busca ligado ao estado: digitar volta à página 1 (`lista.buscar`). */
export function buscaDaLista<F extends Record<string, unknown>>(
  lista: EstadoLista<F>,
  placeholder: string,
  aoMudar: () => void,
): { elemento(): HTMLElement; casa(...campos: (string | null | undefined)[]): boolean } {
  let devolverFoco = false;
  return {
    elemento() {
      const inp = el("input", { type: "search", placeholder, "aria-label": placeholder }) as HTMLInputElement;
      inp.value = lista.busca;
      inp.addEventListener("input", () => {
        lista.buscar(inp.value);
        devolverFoco = true;
        aoMudar();
      });
      if (devolverFoco) {
        devolverFoco = false;
        setTimeout(() => {
          inp.focus();
          const n = inp.value.length;
          inp.setSelectionRange(n, n);
        }, 0);
      }
      return el("div", { class: "busca-lista" }, inp);
    },
    casa: (...campos) => casaBusca(lista.busca, ...campos),
  };
}

/** Os itens do filtro "Situação" dos cadastros (Ativo/Inativo) — um lugar só. */
export const SITUACAO_CADASTRO: readonly Item[] = [
  { id: "ativo", nome: "Ativo" },
  { id: "inativo", nome: "Inativo" },
];
